import { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSquarePollHorizontal,
  faAngleLeft,
} from '@fortawesome/pro-regular-svg-icons';
import { BoxV2 as Box, Flex, CampaignAttributes } from 'portal-commons';

import {
  CampaignSummary,
  CampaignDetailsSection,
  LaunchDetailsSection,
  TechnicalDetailsSection,
  SampleMessagesSection,
  RcsSectionBlock,
  ConnectivityChainSection,
  EventsSection,
  StatusSection,
  CarrierStatusSection,
  CspBrandResellerSection,
  TenDlcCampaignFallbackSection,
} from '../components/RcsCampaignDetail';
import { useCampaignAsset, useRcsVisibility } from '@/hooks';
import { Loader } from '@/shared_elements';
import { CampaignDetail, campaignController } from '@/apis/core';
import { ROUTES, SIDE_NAV_ENTRIES } from '@/constants/paths';
import {
  getRcsUseCases,
  getTenDlcUseCases,
  getVerticalTypes,
} from '@/shared_elements/actions/enumActionCreators';
import { FALLBACK_CAMPAIGN_ENABLED } from '@/constants/index';

const RcsCampaignDetail = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation<{ goBackPage?: string }>();
  const { id: campaignUid } = useParams<{ id: string }>();
  const [pageLoading, setPageLoading] = useState(true);
  const [campaign, setCampaign] = useState<CampaignDetail>();
  const isRcsVisible = useRcsVisibility();

  const rcsUseCasesData = useSelector(
    (state: any) => state.shareReducer.enums.rcsUseCases
  );
  const tenDlcUseCasesData = useSelector(
    (state: any) => state.shareReducer.enums.tenDlcUseCases
  );
  const verticalTypesData = useSelector(
    (state: any) => state.shareReducer.enums.verticalTypes
  );

  const arrayToMap = (
    array: Array<any>,
    keyFrom: string,
    valueFrom: string
  ) => {
    return array.reduce((acc, item) => {
      acc[item[keyFrom]] = item[valueFrom];
      return acc;
    }, {});
  };

  const rcsUsecases = useMemo(() => {
    return arrayToMap(rcsUseCasesData, 'id', 'displayName');
  }, [rcsUseCasesData]);

  const tenDlcUsecases = useMemo(() => {
    return arrayToMap(tenDlcUseCasesData, 'id', 'displayName');
  }, [tenDlcUseCasesData]);

  const verticalTypes = useMemo(() => {
    return arrayToMap(verticalTypesData, 'id', 'displayName');
  }, [verticalTypesData]);

  const [goBackPage] = useState(location.state?.goBackPage || '');
  const goBackLink = (() => {
    const fallbackBackPage = isRcsVisible
      ? SIDE_NAV_ENTRIES.RCS_CAMPAIGNS
      : SIDE_NAV_ENTRIES.DASHBOARD;
    return {
      text:
        isRcsVisible &&
        (!goBackPage || goBackPage.startsWith(ROUTES.RCS_CAMPAIGNS))
          ? 'Back to RCS Campaigns'
          : 'Back',
      path: goBackPage || fallbackBackPage,
    };
  })();

  const { logo, banner } = useCampaignAsset(campaign);

  useEffect(() => {
    dispatch(getRcsUseCases());
    dispatch(getVerticalTypes());
  }, [dispatch]);

  useEffect(() => {
    campaignController
      .getCampaign(campaignUid)
      .then((c) => {
        setCampaign(c);
      })
      .finally(() => {
        setPageLoading(false);
      });
  }, [campaignUid]);

  useEffect(() => {
    if (campaign?.fallbackCampaignUid) {
      dispatch(getTenDlcUseCases());
    }
  }, [campaign?.fallbackCampaignUid, dispatch]);

  if (pageLoading || !campaign) return <Loader />;

  return (
    <Box sx={{ px: 'xxl', py: 'l', width: '100%' }}>
      <Flex
        as="a"
        sx={{
          mb: 'xl',
          width: 'fit-content',
          cursor: 'pointer',
          color: '#999FA0',
        }}
        onClick={() => history.push(goBackLink.path)}
        data-testid="goBackBtn"
      >
        <Box sx={{ mr: 'xs' }}>
          <FontAwesomeIcon icon={faAngleLeft} />
        </Box>
        {goBackLink.text}
      </Flex>
      <CampaignSummary campaign={campaign} usecaseMap={rcsUsecases} />
      <Flex
        sx={{
          flexDirection: 'column',
          gap: 'xl',
        }}
      >
        <CarrierStatusSection campaign={campaign} />
        <StatusSection
          data={{
            rcsVet: campaign?.brand?.rcsVettingStatus,
            logo: campaign?.brand?.logoStatus,
            banner: campaign?.brand?.bannerStatus,
          }}
        />
        <ConnectivityChainSection campaignId={campaignUid} />
        <CspBrandResellerSection
          campaign={campaign}
          verticalTypes={verticalTypes}
        />
        <CampaignDetailsSection
          campaign={campaign}
          logo={{
            name: logo?.name,
            type: 'LOGO',
            verificationStatus: logo?.verificationStatus,
            url: logo?.url,
          }}
          banner={{
            name: banner?.name,
            type: 'BANNER',
            verificationStatus: banner?.verificationStatus,
            url: banner?.url,
          }}
          usecaseMap={rcsUsecases}
        />
        <LaunchDetailsSection campaign={campaign} />
        <TechnicalDetailsSection campaign={campaign} />
        <SampleMessagesSection
          messages={[
            campaign?.sample1,
            campaign?.sample2,
            campaign?.sample3,
            campaign?.sample4,
            campaign?.sample5,
          ].filter((s): s is string => !!s)}
        />
        <RcsSectionBlock
          icon={faSquarePollHorizontal}
          title="Compliance Attributes"
          showDivider={true}
        >
          <CampaignAttributes.ReadonlyPanel
            type="rcs"
            data={{
              ...(campaign as any),
              optinKeywords: campaign?.optinKeywords?.split(',') || [],
              optoutKeywords: campaign?.optoutKeywords?.split(',') || [],
              helpKeywords: campaign?.helpKeywords?.split(',') || [],
            }}
          />
        </RcsSectionBlock>
        {FALLBACK_CAMPAIGN_ENABLED && (
          <TenDlcCampaignFallbackSection
            rcsCampaignUid={campaignUid}
            fallbackCampaignUid={campaign?.fallbackCampaignUid}
            usecaseMap={tenDlcUsecases}
          />
        )}
        <EventsSection campaignUid={campaignUid} />
      </Flex>
    </Box>
  );
};
export default RcsCampaignDetail;
